import { DateType } from 'module/types/common';
import { Filter } from 'module/types/filter-field/filter';
import { DateRange } from 'module/ui/components/molecules/range-filter/range';
import {
  PaginatedRequestParams,
  PaginatedResponse,
} from 'api/models/core-type';
import { AssigneeValue } from '../assignee/types';
import {
  FieldAccessMeta,
  FieldAccessMetaInBase3,
  FieldOption,
  FieldValueType,
} from '../field/types';

export enum LoginPolicyType {
  RocketlaneAuth = 'ROCKETLANE_AUTH',
  MagicLink = 'MAGIC_LINK',
  Idp = 'IDP',
}
export interface BaseAuthenticationType<T> {
  companyId: number;
  authType: T;
  mandatory: boolean;
  enabled: boolean;
  default: boolean;
}
interface IdpAuthenticationType
  extends BaseAuthenticationType<LoginPolicyType.Idp> {
  idpConfigId: number;
}

export type AuthenticationType =
  | IdpAuthenticationType
  | BaseAuthenticationType<LoginPolicyType.MagicLink>
  | BaseAuthenticationType<LoginPolicyType.RocketlaneAuth>;

export enum CompanyType {
  Customer = 'CUSTOMER',
  Partner = 'PARTNER',
  Vendor = 'VENDOR',
}

export interface Company {
  companyName: string;
  companyId: number;
  companyLogoUrl?: string;
  companyLogo?: string;
  companyUrl?: string;
  authentications?: AuthenticationType[];
  companyType: CompanyType;
  strategic?: boolean;
  canTrackTime?: boolean; // this is only applicable to partner company
  createdAt?: number;
}

type UserId = number;

export interface CustomerCompanyFinancialMetrics {
  annualizedRecurringRevenue: number;
  trackedHours: number;
  totalAllocatedHours: number;
  actualRevenue: number;
  estimatedRevenue: number;
  actualProfit: number;
  estimatedProfit: number;
  actualProfitMargin: number;
  estimatedProfitMargin: number;
  actualCost: number;
  estimatedCost: number;
}
export type CustomerCompanyProjectMetrics = { [x: string]: number[] };
enum VisibilityType {
  ALL = 'EVERYONE',
  INVITED = 'MEMBERS',
}
interface FieldValue<T> {
  fieldId: number;
  fieldValue: T;
  metaFieldValue?: FieldOption<T>;
}

export interface CustomerCompanyListFilter extends Filter {
  range?: DateRange;
}

export interface PaginatedCustomerCompaniesParams
  extends PaginatedRequestParams {
  filter: CustomerCompanyListFilter;
  searchText: string;
}

export type PaginatedCustomerCompaniesResponse =
  PaginatedResponse<CustomerCompany>;

export interface CustomerCompany extends Company {
  description: string;
  accountOwner: { userId: UserId };
  customerMembers: UserId[];
  champions: UserId[];
  nextRenewalDate: DateType;
  financialMetrics?: CustomerCompanyFinancialMetrics;
  followers?: AssigneeValue;
  csatRating: number;
  lastMileStoneCSAT: number;
  projectMetrics?: CustomerCompanyProjectMetrics;
  dashboardId: number;
  fields: FieldValue<FieldValueType>[];
  userGroupIds?: number[];
  lastActivityDate?: DateType;
  createdAt: number;
  fieldsAccessMeta: FieldAccessMeta;
  fieldsAccessMetaInBase3: FieldAccessMetaInBase3;
  visibility: VisibilityType;
  // New properties from scarlet/company/list API
  capacity: number;
  capacityMinutes: number;
  identityType: string;
  admins: Array<{
    userId: number;
    identityType: string;
  }>;
  active: boolean;
  maxSeat: number;
  createdBy: {
    userId: number;
    identityType: string;
  };
  updatedBy: {
    userId: number;
    identityType: string;
  };
  lastEngagedOn: string;
  recentRiskSignals: Array<{
    signalName: string;
    signalConfigurationId: number;
    signalOccurrenceCount: number;
    signalOccurrenceIds: number[];
    lastOccurredAt: string;
  }>;
  recentRiskSignalWeeklyCounts: number[];
  recentOpportunitySignals: Array<{
    signalName: string;
    signalConfigurationId: number;
    signalOccurrenceCount: number;
    signalOccurrenceIds: number[];
    lastOccurredAt: string;
  }>;
  recentOpportunitySignalWeeklyCounts: number[];
  recentOperationalSignals: Array<{
    signalName: string;
    signalConfigurationId: number;
    signalOccurrenceCount: number;
    signalOccurrenceIds: number[];
    lastOccurredAt: string;
  }>;
  recentOperationalSignalWeeklyCounts: number[];
  default: boolean;
}

export type CustomerCompanyAPIType = Omit<
  CustomerCompany,
  'fieldsAccessMeta'
> & { fieldsAccessMeta: FieldAccessMeta };

export type CreateCustomerCompanyPayload = Partial<
  Omit<CustomerCompany, 'companyId' | 'companyType' | 'accountOwner'>
> & {
  accountOwner?: number;
};

export type UpdateCustomerCompanyPayload = CreateCustomerCompanyPayload;
